// Components
import {
  Alert,
  Button,
  Group,
  Select,
  Skeleton,
  Stack,
  Textarea,
} from "@mantine/core";
import { IconUserPlus, IconAlertCircle } from "@tabler/icons-react";
import { FormActions } from "@benchmark/ui/components/atomic";
import { FormSection } from "@benchmark/ui/components/atomic";
import { FormField } from "@benchmark/ui/components/atomic";
import { TrimmedTextInput as TextInput } from "@benchmark/ui/components/atomic";
import { PractitionerRoleSearchSelect } from "@benchmark/ui/components/atomic";
import { PractitionerModal } from "@/components/domain/practitioner/Practitioner.modal";

// Constants
import { QUERY_LIMIT_SINGLE } from "@benchmark/core/constants";
import {
  APPOINTMENT_SPECIALTY_SELECT_OPTIONS,
  APPOINTMENT_STATUS_SELECT_OPTIONS,
} from "@benchmark/core/constants";

// Hooks
import { useForm } from "@mantine/form";
import { useMedplum, useMedplumProfile, useResource } from "@medplum/react";
import { useCallback, useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router";
import { useFormSubmission } from "@benchmark/ui/hooks";
import { useFormProtection } from "@benchmark/ui/hooks";
import { useStableParams } from "@/hooks/common/useStableParams";
import { usePatientResource } from "@/hooks/shared/useFHIRResource";
import {
  usePractitionerRoleSearch,
  useHealthcareServiceSearch,
} from "@/hooks/shared/useFHIROptions";

// Types
import type {
  Appointment,
  Patient,
  Practitioner,
  PractitionerRole,
} from "@medplum/fhirtypes";
import type { AppointmentFormData } from "@benchmark/core/types";

// Utils
import {
  showErrorNotification,
  showSuccessNotification,
} from "@benchmark/ui/utils";
import {
  formatDateTimeForInput,
  formatInputForDateTime,
} from "@benchmark/ui/utils";
import { createAppointmentFacade } from "@/facades/appointment.facade";
import { appointmentToForm } from "@benchmark/core/adapters";
import { fetchPractitionerRole } from "@benchmark/core/services";
import { findPrimaryPerformerParticipant } from "@benchmark/core/resolvers";

/**
 * Props for the appointment create/edit form.
 * @agentic connections
 */
type AppointmentFormProps = {
  patientId?: string;
  appointment?: Appointment;
  onSuccess?: (appointmentId: string) => void;
  onCancel?: () => void;
  basedOn?: AppointmentFormData["basedOn"];
  supportingInformation?: AppointmentFormData["supportingInformation"];
};

/**
 * Form component for creating and editing FHIR Appointment resources.
 * @agentic connections
 */
export const AppointmentForm = ({
  patientId: propPatientId,
  appointment: propAppointment,
  onSuccess,
  onCancel,
  basedOn,
  supportingInformation,
}: AppointmentFormProps = {}) => {
  const navigate = useNavigate();
  const medplum = useMedplum();
  const appointmentFacade = useMemo(
    () => createAppointmentFacade(medplum),
    [medplum],
  );
  const profile = useMedplumProfile() as Practitioner;
  const [referrerRole, setReferrerRole] = useState<PractitionerRole>();
  const { patientId: urlPatientId, appointmentId } = useStableParams();

  // Use prop patientId if provided, otherwise fall back to URL
  const effectivePatientId = propPatientId || urlPatientId;
  const { data: patientResource } = usePatientResource(effectivePatientId);
  const patient = patientResource as Patient | undefined;

  // Use prop appointment if provided, otherwise fetch from URL
  const fetchedAppointment = useResource<Appointment>(
    appointmentId ? { reference: `Appointment/${appointmentId}` } : undefined,
  );
  const existingAppointment = propAppointment || fetchedAppointment;
  const [practitionerModalOpened, setPractitionerModalOpened] = useState(false);
  const [noRoleError, setNoRoleError] = useState(false);
  const formLoadedRef = useRef(false);

  // Search query state for async selects
  const [practitionerSearchQuery, setPractitionerSearchQuery] = useState("");
  const [serviceSearchQuery, setServiceSearchQuery] = useState("");

  // Use shared form submission handling
  const { isSubmitting, createSubmissionHandler } = useFormSubmission();

  // Stable form configuration to prevent infinite re-renders
  const formConfig = useMemo(
    () => ({
      initialValues: {
        performer: "",
        provider: "",
        start: "",
        status: "booked" as const,
        "appointment-specialty": "",
        description: "",
        comment: "",
      },
      validate: {
        performer: (value: string | undefined) =>
          !value ? "Practitioner is required" : null,
        start: (value: string | undefined) =>
          !value ? "Date & Time is required" : null,
        status: (value: string | undefined) =>
          !value ? "Status is required" : null,
      },
    }),
    [],
  );

  const form = useForm<AppointmentFormData>(formConfig);

  const { markAsClean, ConfirmationModal } = useFormProtection(form, {
    enabled: true,
    blockNavigation: true,
    confirmationMessage:
      "You have unsaved appointment changes. Are you sure you want to close?",
  });

  // Practitioner role search
  const {
    options: practitionerSearchOptions,
    isSearching: searchingPractitioners,
    resources: practitionerSearchResults,
  } = usePractitionerRoleSearch(practitionerSearchQuery);

  // Healthcare service search
  const {
    options: serviceSearchOptions,
    isSearching: searchingServices,
    resources: serviceSearchResults,
  } = useHealthcareServiceSearch(serviceSearchQuery);

  // Build practitioner options from form state OR search results
  const practitionerRoleOptions = useMemo(() => {
    if (practitionerSearchQuery.trim().length >= 2) {
      return practitionerSearchOptions;
    }
    // When not searching, show selected value from form state
    const selectedId = form.values.performer as string | undefined;
    const selectedLabel = form.values.performerDisplayName as
      string | undefined;
    if (selectedId && selectedLabel) {
      return [{ value: selectedId, label: selectedLabel }];
    }
    return [];
  }, [practitionerSearchQuery, practitionerSearchOptions, form.values]);

  // Build service options from form state OR search results
  const serviceOptions = useMemo(() => {
    if (serviceSearchQuery.trim().length >= 2) {
      return serviceSearchOptions;
    }
    // When not searching, show selected value from form state
    const selectedId = form.values.provider as string | undefined;
    const selectedLabel = form.values.providerDisplayName as string | undefined;
    if (selectedId && selectedLabel) {
      return [{ value: selectedId, label: selectedLabel }];
    }
    return [];
  }, [serviceSearchQuery, serviceSearchOptions, form.values]);

  // Handler to update both ID and display name for practitioner
  const handlePractitionerSelect = useCallback(
    (value: string | null) => {
      form.setFieldValue("performer", value || "");
      if (value) {
        const selected = practitionerSearchResults.find((r) => r.id === value);
        if (selected) {
          form.setFieldValue(
            "performerDisplayName",
            selected.practitioner?.display ||
              selected.code?.[0]?.coding?.[0]?.display ||
              "",
          );
        }
      } else {
        form.setFieldValue("performerDisplayName", "");
      }
    },
    [form, practitionerSearchResults],
  );

  // Handler to update both ID and display name for service
  const handleServiceSelect = useCallback(
    (value: string | null) => {
      form.setFieldValue("provider", value || "");
      if (value) {
        const selected = serviceSearchResults.find((r) => r.id === value);
        if (selected) {
          form.setFieldValue("providerDisplayName", selected.name || "");
        }
      } else {
        form.setFieldValue("providerDisplayName", "");
      }
    },
    [form, serviceSearchResults],
  );

  // TODO(#990): Fetch the PractitionerRole for the logged-in user using Resource hook
  useEffect(() => {
    if (!profile) return;

    medplum
      .searchResources("PractitionerRole", {
        practitioner: `Practitioner/${profile.id}`,
        active: "true",
        _count: QUERY_LIMIT_SINGLE.toString(),
        _sort: "-_lastUpdated",
      })
      .then((roles) => {
        if (roles[0]) {
          setReferrerRole(roles[0]);
          setNoRoleError(false);
        } else {
          setNoRoleError(true);
        }
      })
      .catch(() => {
        showErrorNotification(
          "Error fetching your PractitionerRole. Please try again or contact support.",
        );
      });
  }, [medplum, profile]);

  // Reset form loaded flag when appointment changes
  useEffect(() => {
    formLoadedRef.current = false;
  }, [existingAppointment?.id]);

  // Pre-fill form if editing existing appointment
  useEffect(() => {
    if (!existingAppointment || !referrerRole || formLoadedRef.current) {
      return;
    }

    const loadFormData = async () => {
      try {
        // Find the primary performer participant
        const performerParticipant = findPrimaryPerformerParticipant(
          existingAppointment,
          referrerRole.id,
        );

        // Resolve PractitionerRole ID if participant references a Practitioner (legacy)
        let resolvedPerformerRoleId: string | undefined;
        let performerDisplayName: string | undefined;

        if (performerParticipant?.actor?.reference) {
          const reference = performerParticipant.actor.reference;

          // If it's already a PractitionerRole reference, extract the ID
          const roleMatch = reference.match(/PractitionerRole\/(.+)/);
          if (roleMatch) {
            resolvedPerformerRoleId = roleMatch[1];
            performerDisplayName = performerParticipant.actor.display;
          } else {
            // If it's a Practitioner reference (legacy), resolve to PractitionerRole
            const practitionerMatch = reference.match(/Practitioner\/(.+)/);
            if (practitionerMatch) {
              try {
                const practitionerRole = await fetchPractitionerRole(
                  medplum,
                  practitionerMatch[1],
                );
                resolvedPerformerRoleId = practitionerRole.id;
                performerDisplayName =
                  performerParticipant.actor.display ||
                  practitionerRole.practitioner?.display;
              } catch (error) {
                console.error(
                  "Error finding practitioner role for appointment:",
                  error,
                );
                // Don't fail the whole conversion if we can't find the role
              }
            }
          }
        }

        // Use pure adapter for transformation
        const formData = appointmentToForm(existingAppointment, {
          currentUserRoleId: referrerRole.id,
          resolvedPerformerRoleId,
          performerDisplayName,
        });

        form.setValues(formData);
        formLoadedRef.current = true;
      } catch (error) {
        console.error("Error loading appointment form data:", error);
        showErrorNotification("Failed to load appointment data");
      }
    };

    loadFormData();
  }, [
    existingAppointment,
    referrerRole,
    medplum,
    // Removed 'form' from dependencies - it's not stable and causes infinite loops
  ]);

  const handleSubmit = useCallback(
    async (formData: AppointmentFormData) => {
      if (!patient || !referrerRole) {
        showErrorNotification(
          "Patient and referring practitioner role are required",
        );
        return;
      }

      const enrichedFormData: AppointmentFormData = {
        ...formData,
        ...(basedOn && { basedOn }),
        ...(supportingInformation && { supportingInformation }),
      };

      const appointment = await appointmentFacade.upsert(
        enrichedFormData,
        patient,
        referrerRole,
        existingAppointment as Appointment | undefined,
      );

      showSuccessNotification(
        `Appointment ${existingAppointment ? "updated" : "created"}.`,
      );

      markAsClean();

      if (onSuccess && appointment.id) {
        onSuccess(appointment.id);
      } else {
        navigate(`/Patient/${patient.id}/Appointment/${appointment.id}`);
      }
    },
    [
      appointmentFacade,
      navigate,
      patient,
      referrerRole,
      existingAppointment,
      onSuccess,
      basedOn,
      supportingInformation,
      markAsClean,
    ],
  );

  const handlePractitionerCreated = (
    practitioner: Practitioner,
    practitionerRole: PractitionerRole,
  ) => {
    // Set the newly created practitioner role
    if (practitionerRole.id) {
      form.setFieldValue("performer", practitionerRole.id);
      form.setFieldValue(
        "performerDisplayName",
        practitionerRole.practitioner?.display || "",
      );
    }
    setPractitionerModalOpened(false);
  };

  if (!patient) {
    return (
      <Stack gap="md">
        <Skeleton height={32} width="40%" />
        <Skeleton height={14} width="30%" />
        <Skeleton height={36} />
        <Skeleton height={14} width="25%" />
        <Skeleton height={36} />
        <Skeleton height={44} width="200px" />
      </Stack>
    );
  }

  if (noRoleError) {
    return (
      <Alert
        icon={<IconAlertCircle size="1rem" />}
        title="Missing PractitionerRole"
        color="red"
      >
        You don't have an active PractitionerRole. Please contact your
        administrator to set up your role before creating appointments.
      </Alert>
    );
  }

  if (!referrerRole) {
    return (
      <Stack gap="md">
        <Skeleton height={32} width="40%" />
        <Skeleton height={14} width="30%" />
        <Skeleton height={36} />
        <Skeleton height={14} width="25%" />
        <Skeleton height={36} />
        <Skeleton height={44} width="200px" />
      </Stack>
    );
  }

  return (
    <>
      <form onSubmit={form.onSubmit(createSubmissionHandler(handleSubmit))}>
        <Stack gap="md">
          {/* Provider Selection Section */}
          <FormSection
            title="Provider Selection"
            subtitle="Select the practitioner and healthcare service"
          >
            <Group grow>
              <Group align="end">
                <div style={{ flex: 1 }}>
                  <FormField label="Practitioner" required>
                    <PractitionerRoleSearchSelect
                      options={practitionerRoleOptions}
                      value={form.values.performer || null}
                      searchQuery={practitionerSearchQuery}
                      isSearching={searchingPractitioners}
                      onChange={handlePractitionerSelect}
                      onSearchChange={setPractitionerSearchQuery}
                      placeholder="Search practitioners..."
                      noResultsMessage="No practitioners found"
                      error={form.errors.performer}
                    />
                  </FormField>
                </div>
                <Button
                  variant="outline"
                  onClick={() => setPractitionerModalOpened(true)}
                  size="sm"
                  style={{
                    marginBottom: form.errors.performer ? "25px" : "0px",
                  }}
                >
                  <IconUserPlus size="1rem" />
                </Button>
              </Group>

              <FormField label="Healthcare Service" required>
                <Select
                  placeholder={
                    serviceSearchQuery.trim().length < 2
                      ? "Type at least 2 characters to search"
                      : searchingServices
                        ? "Searching..."
                        : "Search services..."
                  }
                  data={serviceOptions}
                  value={form.values.provider}
                  onChange={handleServiceSelect}
                  onSearchChange={setServiceSearchQuery}
                  searchable
                  clearable
                  filter={({ options }) => options} // Disable client-side filtering
                  nothingFoundMessage={
                    serviceSearchQuery.trim().length < 2
                      ? "Type at least 2 characters to search"
                      : searchingServices
                        ? "Searching..."
                        : "No services found"
                  }
                  error={form.errors.provider}
                />
              </FormField>
            </Group>
          </FormSection>

          {/* Appointment Details Section */}
          <FormSection
            title="Appointment Details"
            subtitle="Schedule and status information"
          >
            <Group grow>
              <FormField label="Date & Time" required>
                <TextInput
                  type="datetime-local"
                  placeholder="Select appointment date and time"
                  value={
                    form.values.start
                      ? formatDateTimeForInput(form.values.start)
                      : ""
                  }
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value) {
                      const isoValue = formatInputForDateTime(value);
                      form.setFieldValue("start", isoValue);
                    } else {
                      form.setFieldValue("start", "");
                    }
                  }}
                  error={form.errors.start}
                />
              </FormField>

              <FormField label="Status" required>
                <Select
                  placeholder="Select status"
                  data={APPOINTMENT_STATUS_SELECT_OPTIONS}
                  value={form.values.status}
                  onChange={(value) =>
                    form.setFieldValue(
                      "status",
                      value as AppointmentFormData["status"],
                    )
                  }
                  searchable
                  clearable
                  error={form.errors.status}
                />
              </FormField>
            </Group>

            <FormField label="Specialty">
              <Select
                placeholder="Select specialty"
                data={APPOINTMENT_SPECIALTY_SELECT_OPTIONS}
                value={form.values["appointment-specialty"]}
                onChange={(value) =>
                  form.setFieldValue("appointment-specialty", value || "")
                }
                searchable
                clearable
              />
            </FormField>
          </FormSection>

          {/* Additional Information Section */}
          <FormSection
            title="Additional Information"
            subtitle="Description and comments"
            collapsible={true}
            defaultCollapsed={true}
          >
            <FormField label="Description">
              <TextInput
                placeholder="Brief description of the appointment"
                value={form.values.description}
                onChange={(e) =>
                  form.setFieldValue("description", e.target.value)
                }
              />
            </FormField>

            <FormField label="Additional Comments">
              <Textarea
                placeholder="Any additional notes or comments"
                rows={3}
                value={form.values.comment}
                onChange={(e) => form.setFieldValue("comment", e.target.value)}
              />
            </FormField>
          </FormSection>

          <FormActions
            isSubmitting={isSubmitting}
            resourceType="Appointment"
            isEditing={!!existingAppointment}
            showCancel={true}
            onCancel={onCancel}
          />
        </Stack>
      </form>

      <PractitionerModal
        opened={practitionerModalOpened}
        onClose={() => setPractitionerModalOpened(false)}
        onPractitionerCreated={handlePractitionerCreated}
      />

      <ConfirmationModal />
    </>
  );
};
